import React, { useEffect, useState } from 'react';
import { ArrowLeft, Ban, CheckCircle2, Loader2, Plus, ShieldAlert, UserPlus } from 'lucide-react';
import {
  ALL_ROLES,
  ROLE_LABEL,
  TenantUser,
  createTenantUser,
  disableTenantUser,
  enableTenantUser,
  fetchTenantUsers,
  updateTenantUserRoles,
} from '../admin';
import { Role } from '../types';

interface UserManagementProps {
  onBack: () => void;
}

const RoleChips: React.FC<{
  selected: Role[];
  onToggle: (role: Role) => void;
  disabled?: boolean;
}> = ({ selected, onToggle, disabled }) => (
  <div className="flex flex-wrap gap-1.5">
    {ALL_ROLES.map((role) => (
      <button
        key={role}
        type="button"
        disabled={disabled}
        onClick={() => onToggle(role)}
        className={`rounded-lg border px-2.5 py-1 text-xs font-semibold transition-all disabled:opacity-40 ${
          selected.includes(role)
            ? 'border-cyan-600 bg-cyan-950 text-cyan-300'
            : 'border-slate-700 text-slate-400 hover:border-slate-500'
        }`}
      >
        {ROLE_LABEL[role]}
      </button>
    ))}
  </div>
);

/**
 * Tenant admin's user roster (§7.1). Creating a user here does both steps that used to need a
 * shell — mint the sign-in identity and map its role — in one authenticated action.
 */
export const UserManagement: React.FC<UserManagementProps> = ({ onBack }) => {
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRoles, setNewRoles] = useState<Role[]>([]);
  const [creating, setCreating] = useState(false);
  const [createdNotice, setCreatedNotice] = useState<string | null>(null);

  const load = async () => {
    try {
      setUsers(await fetchTenantUsers());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => void load(), []);

  const toggleNewRole = (role: Role) =>
    setNewRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim() || !newName.trim() || newRoles.length === 0) {
      setError('Email, name and at least one role are required.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await createTenantUser(newEmail.trim(), newName.trim(), newRoles);
      setCreatedNotice(
        `Created ${newEmail.trim()}. Cognito emailed them a temporary password (or, if this address ` +
          `already existed there, their roles were just updated to match).`,
      );
      setNewEmail('');
      setNewName('');
      setNewRoles([]);
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the user.');
    } finally {
      setCreating(false);
    }
  };

  const toggleRole = async (user: TenantUser, role: Role) => {
    const roles = user.roles.includes(role)
      ? user.roles.filter((r) => r !== role)
      : [...user.roles, role];
    if (roles.length === 0) return; // a user must keep at least one role
    setBusyId(user.id);
    try {
      const updated = await updateTenantUserRoles(user.id, roles);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update roles.');
    } finally {
      setBusyId(null);
    }
  };

  const toggleEnabled = async (user: TenantUser) => {
    setBusyId(user.id);
    try {
      const updated = await (user.status === 'ACTIVE' ? disableTenantUser : enableTenantUser)(user.id);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change account status.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4 px-4 py-6">
      <div className="glass-panel flex items-center justify-between rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="rounded-xl border border-slate-800 bg-slate-900 p-2 text-slate-300 hover:text-white">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-white">Users</h2>
            <p className="text-xs text-slate-400">Create clinicians and set their roles. No shell access needed.</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="flex items-center gap-1.5 rounded-lg border border-cyan-700 bg-cyan-800 px-3 py-2 text-xs font-semibold text-white"
        >
          <UserPlus className="h-3.5 w-3.5" /> Add user
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-800 bg-rose-950 px-4 py-2 text-xs text-rose-200">{error}</div>
      )}
      {createdNotice && (
        <div className="flex items-center justify-between rounded-xl border border-emerald-800 bg-emerald-950 px-4 py-2 text-xs text-emerald-200">
          <span>{createdNotice}</span>
          <button onClick={() => setCreatedNotice(null)}>Dismiss</button>
        </div>
      )}

      {showCreate && (
        <form onSubmit={submitCreate} className="glass-panel space-y-3 rounded-2xl p-4 text-xs">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block font-semibold text-slate-300">Email</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-slate-200 focus:outline-none focus:border-cyan-500"
                required
              />
            </div>
            <div>
              <label className="mb-1 block font-semibold text-slate-300">Full name</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-slate-200 focus:outline-none focus:border-cyan-500"
                required
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block font-semibold text-slate-300">Roles</label>
            <RoleChips selected={newRoles} onToggle={toggleNewRole} />
            <p className="mt-1 text-[11px] text-slate-500">
              Physician can review, edit and sign. Technician can review and annotate, not sign — Physician
              already includes everything Technician can do. Admin manages users and templates, and does not
              by itself grant access to patient data.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowCreate(false)} className="rounded-xl px-4 py-2 text-slate-300">
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="flex items-center gap-1.5 rounded-xl bg-cyan-600 px-4 py-2 font-semibold text-white disabled:opacity-50"
            >
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Create
            </button>
          </div>
        </form>
      )}

      <div className="glass-panel overflow-hidden rounded-2xl">
        <table className="w-full text-left text-xs">
          <thead className="border-b border-slate-800 bg-slate-900/80 uppercase text-[10px] tracking-wider text-slate-400">
            <tr>
              <th className="p-3">User</th>
              <th className="p-3">Roles</th>
              <th className="p-3">PHI access</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {loading && (
              <tr><td colSpan={5} className="p-6 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-slate-400">No users yet. Add one above.</td></tr>
            )}
            {users.map((user) => (
              <tr key={user.id} className={user.status === 'DISABLED' ? 'opacity-50' : ''}>
                <td className="p-3">
                  <div className="font-semibold text-white">{user.full_name}</div>
                  <div className="mono text-slate-400">{user.email}</div>
                </td>
                <td className="p-3">
                  <RoleChips
                    selected={user.roles}
                    disabled={busyId === user.id || user.status === 'DISABLED'}
                    onToggle={(role) => void toggleRole(user, role)}
                  />
                </td>
                <td className="p-3">
                  {user.can_read_phi ? (
                    <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> Yes</span>
                  ) : (
                    <span className="flex items-center gap-1 text-slate-500"><ShieldAlert className="h-3.5 w-3.5" /> No</span>
                  )}
                </td>
                <td className="p-3">
                  <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${user.status === 'ACTIVE' ? 'border border-emerald-800 bg-emerald-950 text-emerald-300' : 'border border-slate-700 text-slate-400'}`}>
                    {user.status}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <button
                    disabled={busyId === user.id}
                    onClick={() => void toggleEnabled(user)}
                    className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-slate-300 hover:border-slate-500 disabled:opacity-40"
                    title={user.status === 'ACTIVE' ? 'Disable — never deleted, so the audit trail stays intact' : 'Re-enable'}
                  >
                    {user.status === 'ACTIVE' ? <Ban className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                    {user.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
